import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "src/app/services/api.service";
import { LanguageService } from "src/app/services/language.service";
import { RoutingService } from "src/app/services/routing.service";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { Entity } from "src/app/models/mvp-query-response-dto";
import { EntityType } from "src/app/enums/entity-type.enum";
import { SurveyPageDto } from "src/app/models/survey-page-dto";
import { ControlType } from '../arcgis-map/constants/control-type.enum';
import { MapControlItem } from '../arcgis-map/constants/map-control-item.interface';
import { ARCGIS_MAP_LIFECYCLE } from '../arcgis-map/constants/map-life-cycle.interface';
import { MainMapService } from '../arcgis-map/services/main-map.service';

@Component({
  selector: 'survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  providers: [
    {
      provide: ARCGIS_MAP_LIFECYCLE,
      useClass: MainMapService,
    },
  ],
})
export class SurveyComponent implements OnInit, OnDestroy {    
    private entityDetailsSubscription?: Subscription;
    private languageSubscription?: Subscription;
    private currentLanguageSubscription?: Subscription;
    closeClicked: Subject<void> = new Subject<void>();
    switchToMapTab: Subject<void> = new Subject<void>();
    @Output() onShapeLeftClick = new EventEmitter<any[]>();
    @Output() onClose = new EventEmitter<void>();
    selectedSurveyDetails!: SurveyPageDto;
    surveyDetailsIsLoading: boolean = true;
    entityId: number = -1;
    entity: Entity = {} as Entity;
    entityType: string = EntityType.SURVEY;
    currentLanguage$!: Observable<string>;
    currentLang: 'he' | 'en' = 'he';
    currentLanguage: 'he' | 'en' = 'he';
    shapes: any[] = [];
    mapControls: MapControlItem[] = [];
    selectedEntityId: number | null = null;
    highlightedShapeId: string | null = null;

  constructor(
     private routingService: RoutingService,
     private apiService: ApiService,
     private translate: TranslateService,
     private languageService: LanguageService,
     private cdr: ChangeDetectorRef,
     private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.currentLanguage$ = this.languageService.currentLanguage$;

    this.languageSubscription = this.currentLanguage$.subscribe(lang => {
      this.currentLang = lang as 'he' | 'en';
    });

    this.currentLanguageSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang as 'he' | 'en';
      this.populateMapControls();
    });

    this.entityDetailsSubscription = this.routingService.entityDetails$
      .subscribe(entityDetails => {
        this.ngZone.run(() => {
          const { id, type } = entityDetails;
          if (!!id && [EntityType.SURVEY].includes(type as EntityType)) {
            this.entityId = id;
            this.entityType = type;
            this.cdr.detectChanges();
            this.getSurveyDetails(id);
          }
        });
      });
  }

  getSurveyDetails(entityId: number) {
    this.surveyDetailsIsLoading = true;
    this.apiService.getSurveyPage(entityId)
      .pipe(takeUntil(this.closeClicked))
      .subscribe({
        next: (res: SurveyPageDto) => {
          this.selectedSurveyDetails = res;
          this.buildShapes();
          this.stopLoading();
        },
        error: () => this.stopLoading(),
        complete: () => {
          this.stopLoading(); 
        }
      });
  }

  buildShapes() {
    this.shapes = [];
    
    if (this.selectedSurveyDetails?.mapInfo) {
      const mapInfo = this.selectedSurveyDetails.mapInfo;
      
      // Add yazam report polygons
      mapInfo.yazamReportShapes?.forEach((shape: any) => {
        const report = this.selectedSurveyDetails.yazamReports.find(r => r.entityId === shape.shapeData?.entity?.entityId);
        const isHighlighted = this.selectedEntityId === shape.shapeData?.entity?.entityId;
        
        // Handle multilingual reportTitle - ensure we get a string
        let reportTitle = '';
        if (report?.reportTitle) {
          if (typeof report.reportTitle === 'string') {
            reportTitle = report.reportTitle;
          } else if (typeof report.reportTitle === 'object') {
            reportTitle = this.currentLang === 'he' 
              ? (report.reportTitle.hebrew || report.reportTitle.english || '')
              : (report.reportTitle.english || report.reportTitle.hebrew || '');
          }
        }
        
        this.shapes.push({
          wkt: shape.shapeWkt,
          layerName: this.translate.instant('iaa.app.survey.reportLayer'),
          shapeId: `report-${shape.shapeData?.entity?.entityId}`,
          hidden: false,
          shapeData: shape.shapeData,
          popupTemplate: {
            title: reportTitle || 'Report'
          },
          styledParams: {
            fillColor: isHighlighted ? [144, 238, 144, 0.5] : [144, 238, 144, 0.3],
            borderColor: isHighlighted ? [60, 179, 113, 1] : [60, 179, 113, 0.8],
            markerSize: isHighlighted ? 14 : 10,
            markerOutlineColor: [60, 179, 113, 1],
            markerOutlineWidth: isHighlighted ? 3 : 2,
          }
        });
      });

      // Add license shapes
      this.selectedSurveyDetails.licenses?.forEach((license: any) => {
        license.shapes?.forEach((shape: any) => {
          const isHighlighted = this.selectedEntityId === license.licenseId;
          
          this.shapes.push({
            wkt: shape.wkt,
            layerName: this.translate.instant('iaa.app.survey.licenseLayer'),
            shapeId: `license-${license.licenseId}`,
            hidden: false,
            shapeData: {
              entity: {
                entityId: license.licenseId,
                entityType: 'license'
              }
            },
            popupTemplate: {
              title: license.licenseLongName || license.licenseShortName || 'License'
            },
            styledParams: {
              fillColor: isHighlighted ? [255, 200, 100, 0.5] : [255, 200, 100, 0.3],
              borderColor: isHighlighted ? [200, 150, 50, 1] : [200, 150, 50, 0.8],
              markerSize: isHighlighted ? 14 : 10,
              markerOutlineColor: [200, 150, 50, 1],
              markerOutlineWidth: isHighlighted ? 3 : 2,
            }
          });
        });
      });

      // Add survey polygon (merged from all license shapes)
      if (mapInfo.surveyPolygon) {
        const isHighlighted = this.selectedEntityId === this.selectedSurveyDetails.entityId;
        
        // Handle survey name - ensure we get a string
        let surveyTitle = this.currentLang === 'he' 
          ? (this.selectedSurveyDetails.surveyNameHebrew || this.selectedSurveyDetails.surveyNameEnglish || '')
          : (this.selectedSurveyDetails.surveyNameEnglish || this.selectedSurveyDetails.surveyNameHebrew || '');
        
        this.shapes.push({
          wkt: mapInfo.surveyPolygon.shapeWkt,
          layerName: this.translate.instant('iaa.app.survey.surveyLayer'),
          shapeId: `survey-${this.selectedSurveyDetails.surveyId}`,
          hidden: false,
          shapeData: { 
            ...mapInfo.surveyPolygon.shapeData,
            entity: {
              entityId: this.selectedSurveyDetails.entityId,
              entityType: 'survey',
              title: {
                hebrew: this.selectedSurveyDetails.surveyNameHebrew,
                english: this.selectedSurveyDetails.surveyNameEnglish
              }
            }
          },
          popupTemplate: {
            title: surveyTitle || 'Survey'
          },
          styledParams: {
            fillColor: isHighlighted ? [100, 150, 255, 0.4] : [100, 150, 255, 0.2],
            borderColor: isHighlighted ? [50, 100, 200, 1] : [50, 100, 200, 0.9],
            borderWidth: isHighlighted ? 4 : 3,
          }
        });
      }
    }
  }

  createSurveyPopupContent(): string {
    const parts: string[] = [];
    
    // Add survey type
    if (this.selectedSurveyDetails.surveyTypeName || this.selectedSurveyDetails.surveyType) {
      const typeDisplay = this.selectedSurveyDetails.surveyTypeName || this.selectedSurveyDetails.surveyType;
      parts.push(`<strong>${this.translate.instant('iaa.app.survey.surveyType')}:</strong> ${typeDisplay}<br/>`);
    }
    
    // Add years
    if (this.selectedSurveyDetails.years) {
      parts.push(`<strong>${this.translate.instant('iaa.app.survey.years')}:</strong> ${this.selectedSurveyDetails.years}<br/>`);
    }
    
    // Add responsible person
    if (this.selectedSurveyDetails.responsiblePerson) {
      const personName = this.currentLang === 'he' 
        ? (this.selectedSurveyDetails.responsiblePerson.nameHebrew || this.selectedSurveyDetails.responsiblePerson.nameEnglish)
        : (this.selectedSurveyDetails.responsiblePerson.nameEnglish || this.selectedSurveyDetails.responsiblePerson.nameHebrew);
      if (personName) {
        parts.push(`<strong>${this.translate.instant('iaa.app.survey.responsiblePerson')}:</strong> ${personName}<br/>`);
      }
    }
    
    // Add licenses count
    if (this.selectedSurveyDetails.licenses?.length > 0) {
      parts.push(`<strong>${this.translate.instant('iaa.general.licenses')}:</strong> ${this.selectedSurveyDetails.licenses.length}<br/>`);
    }
    
    // Add yazam reports count
    if (this.selectedSurveyDetails.yazamReports?.length > 0) {
      parts.push(`<strong>${this.translate.instant('iaa.app.survey.yazamReports')}:</strong> ${this.selectedSurveyDetails.yazamReports.length}<br/>`);
    }
    
    return parts.join('');
  }

  stopLoading() {
    this.surveyDetailsIsLoading = false;
  }

  highlightShape(entityId: number, entityType: string) {
    // Switch to map tab on mobile
    this.switchToMapTab.next();
    
    // Toggle behavior: if clicking the same entity, deselect it
    if (this.selectedEntityId === entityId) {
      this.selectedEntityId = null;
    } else {
      this.selectedEntityId = entityId;
    }
    
    // Rebuild shapes to apply highlighting
    this.buildShapes();
  }

  
  populateMapControls(): void {
    const position = this.currentLanguage === 'he' ? 'top-left' : 'top-right';
    this.mapControls = [
      { position: position, type: ControlType.FullScreen },
      { position: position, type: ControlType.Zoom },
      {
        position: position,
        type: ControlType.Locate,
        attributes: { scale: 100 },
      },
      {
        position: position,
        type: ControlType.BasemapGallery,
        expand: {
          id: 'basemap-gallery-expand',
          position: position,
          expandIcon: 'basemap',
          expandTooltip: `${this.translate.instant('iaa.map.expand-basemap-gallery')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.LayerList,
        expand: {
          id: 'layer-list-expand',
          position: position,
          expandIcon: 'layers',
          expandTooltip: `${this.translate.instant('iaa.map.expand-layers')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.Print,
        attributes: {
          allowedFormats: 'all',
          allowedLayouts: 'all',
          printServiceUrl:
            'https://gis.israntique.org.il/arcgis/rest/services/IAAPrintTemplates/IAAPrintTemplateGrid25000/GPServer/Export%20Web%20Map',
        },
        expand: {
          position: position,
          expandIcon: 'print',
          expandTooltip: `${this.translate.instant('iaa.map.expand-print')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.AreaMeasurement2D,
        attributes: {
          unit: 'metric',
          locale: this.currentLanguage
        },
        expand: {
          position: position,
          expandIcon: 'polygon',
          expandTooltip: `${this.translate.instant('iaa.map.expand-area-measurement')}`,
          mode: 'floating',
        },
      },
      {
        position: position,
        type: ControlType.DistanceMeasurement2D,
        attributes: {
          unit: 'metric',
          locale: this.currentLanguage
        },
        expand: {
          position: position,
          expandIcon: 'measure-line',
          expandTooltip: `${this.translate.instant('iaa.map.expand-distance-measurement')}`,
          mode: 'floating',
        },
      },
      {
        position: this.currentLanguage === 'he' ? 'bottom-left' : 'bottom-right',
        type: ControlType.CoordinateConversion
      },
    ];
  }

  ngOnDestroy(): void {
    this.entityDetailsSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
    this.currentLanguageSubscription?.unsubscribe();
  }

  goToSurveys(): void {
    const surveyPolygon = this.selectedSurveyDetails?.mapInfo?.surveyPolygon?.shapeWkt;
    if (!surveyPolygon) {
      console.warn('No survey polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'surveys',
      regions: surveyPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  goToExcavations(): void {
    const surveyPolygon = this.selectedSurveyDetails?.mapInfo?.surveyPolygon?.shapeWkt;
    if (!surveyPolygon) {
      console.warn('No survey polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'excavations',
      regions: surveyPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  goToSites(): void {
    const surveyPolygon = this.selectedSurveyDetails?.mapInfo?.surveyPolygon?.shapeWkt;
    if (!surveyPolygon) {
      console.warn('No survey polygon available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'hachrazot',
      regions: surveyPolygon,
      tabId: 0,
      entity_index: 0
    });
  }

  hasSurveyPolygon(): boolean {
    return !!this.selectedSurveyDetails?.mapInfo?.surveyPolygon?.shapeWkt;
  }

  goToObjects(): void {
    const licenses = this.selectedSurveyDetails?.licenses?.map(l => l.licenseId).join(',');
    if (!licenses) {
      console.warn('No licenses available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'objects',
      surveyLicenses: licenses,
      tabId: 0,
      entity_index: 0
    });
  }

  goToArchiveDocuments(): void {
    const licenses = this.selectedSurveyDetails?.licenses?.map(l => l.licenseId).join(',');
    if (!licenses) {
      console.warn('No licenses available');
      return;
    }
    
    this.routingService['navigateWithReplace']({
      entityTypes: 'scientific_archive_documents',
      surveyLicenses: licenses,
      tabId: 0,
      entity_index: 0
    });
  }

  getDetailsTitle(): string {
    const prefix = this.translate.instant('iaa.app.survey.title') as string;
    
    const title: string = this.currentLang === 'he' 
      ? (this.selectedSurveyDetails?.surveyNameHebrew || this.selectedSurveyDetails?.surveyNameEnglish || '')
      : (this.selectedSurveyDetails?.surveyNameEnglish || this.selectedSurveyDetails?.surveyNameHebrew || '');
    
    return title ? `${prefix} - ${title}` : prefix;
  }

  getMapTitle(): string {
    return this.translate.instant('iaa.app.survey.mapTitle') as string;
  }
}
